import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import Login from './pages/Login';
import SplashScreen from './components/SplashScreen';
import { isAuthenticated, clearAuth, getUser } from './utils/auth';
import api from './utils/api';
import { fetchStagioniDefault } from './utils/stagioni';
import { subscribeToPush } from './utils/notifications';
import { Toaster } from 'sonner';
import './App.css';
import './motion.css';
import { ContattiProvider } from './context/ContattiContext';
import { PdfViewerProvider } from './context/PdfViewerContext';
import ErrorBoundary from './components/ErrorBoundary';

// Lazy-loaded sections
const ContattiClientiSection = lazy(() => import('./components/ContattiClientiSection'));
const AmministrazioneSection = lazy(() => import('./components/AmministrazioneSection'));
const BloccatiSection = lazy(() => import('./components/BloccatiSection'));
// Sezione 'Saldi Sospesi' rimossa: ora i saldi vengono importati direttamente in Bloccati.
const GestioneEffettiUnificato = lazy(() => import('./components/GestioneEffettiUnificato'));
const GestioneClientiSection = lazy(() => import('./components/GestioneClientiSection'));
const RecruitingSection = lazy(() => import('./components/RecruitingSection'));
const CalendarioSection = lazy(() => import('./components/CalendarioSection'));
const AppuntamentiSection = lazy(() => import('./components/AppuntamentiSection'));
const ProgettiSpecialiSection = lazy(() => import('./components/ProgettiSpecialiSection'));
const ImpostazioniSection = lazy(() => import('./components/ImpostazioniSection'));
const ContabilitaSection = lazy(() => import('./components/ContabilitaSection'));
const DDTSection = lazy(() => import('./components/DDTSection'));
const RiepilogoClientiSection = lazy(() => import('./components/RiepilogoClientiSection'));
const MultiLineaClientiSection = lazy(() => import('./components/MultiLineaClientiSection'));
const QuintiliSection = lazy(() => import('./components/QuintiliSection'));
const RiassortimentiSection = lazy(() => import('./components/RiassortimentiSection'));
const SchedeCommercialiSection = lazy(() => import('./components/SchedeCommercialiSection'));
const MappaClientiSection = lazy(() => import('./components/MappaClientiSection'));
const RicercaSection = lazy(() => import('./components/RicercaSection'));
const HomeView = lazy(() => import('./components/HomeView'));
const BudgetSection = lazy(() => import('./components/BudgetSection'));
const CampionariSection = lazy(() => import('./components/CampionariSection'));
const GestioneShowroomSection = lazy(() => import('./components/GestioneShowroomSection'));
import { 
  Home, Users, CreditCard, UserCheck, UserPlus, Target, MapPin,
  TrendingUp, ChevronDown, ChevronRight, LogOut, Calendar,
  DollarSign, ShoppingBag, User, FileText, BarChart3, Menu, X, Search, Bell, Lock, CheckSquare, Settings,
  Sun, Moon, Package
} from 'lucide-react';

// Province Lombardia
const PROVINCE = [
  "Bergamo", "Brescia", "Como", "Cremona", "Lecco", "Lodi",
  "Mantova", "Milano", "Monza e Brianza", "Pavia", "Sondrio", "Varese"
];

const SectionLoader = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '80px 0' }}>
    <div style={{ width: '32px', height: '32px', border: '3px solid var(--border-color)', borderTopColor: 'var(--accent-green)', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
  </div>
);

// Agenti
const AGENTI = ["Andrea", "Lorella", "Pier Angelo", "Stefano"];

// Tipi Appuntamento
const TIPI_APPUNTAMENTO = ["Collezione Intera", "Prima Uscita", "Seconda Uscita"];

// Tipi Cliente
const TIPI_CLIENTE = ["Nuovo", "Storico"];

// Comparti Sarabanda (alfabetico)
const COMPARTI_SARABANDA = [
  { key: 'activewear', label: 'ActiveWear' },
  { key: 'capsule', label: 'Capsule' },
  { key: 'cerimonia', label: 'Cerimonia' },
  { key: 'main', label: 'Main' },
  { key: 'minibanda', label: 'MiniBanda' },
  { key: 'pre_coll', label: 'Off. Comm.' }
];

// Comparti iDO (alfabetico)
const COMPARTI_IDO = [
  { key: 'ido_mini', label: 'iDO Mini' },
  { key: 'main', label: 'Main' },
  { key: 'offerta_commerciale', label: 'Offerta Commerciale' },
  { key: 'pre_coll', label: 'Pre-coll' }
];

// Modalità Pagamento (alfabetico)
const MODALITA_PAGAMENTO = ["Assegno", "Bonifico", "Bonifico + Assegno", "RID", "Ri.Ba"];

// Tipi Contatto Recruiting
const TIPI_CONTATTO = ["Conoscenza", "Interessato", "Obiettivo", "Priorità"];

// Soluzioni Recruiting (alfabetico)
const SOLUZIONI_RECRUITING = [
  "Appuntamento", "Attendere contatto", "Impossibile", 
  "Ricontattare", "Scrivere mail", "Visitare"
];

// Info Bancarie
const INFO_BANCARIE = ["Negative", "Medie", "Positive"];

// Tipi Progetto (alfabetico)
const TIPI_PROGETTO = [
  "Altro", "Apertura nuova filiale", "Collaborazione esclusiva",
  "Espansione gamma prodotti", "Evento speciale/Sfilata", "Rinnovo contratto importante"
];

// Priorità Progetto
const PRIORITA_PROGETTO = ["Alta", "Bassa", "Media"];

// Stati Progetto
const STATI_PROGETTO = ["Avanzato", "Completato", "In corso", "Iniziale"];

// Categorie Entrata
const CATEGORIE_ENTRATA = ["Altro ricavo", "Fatturato", "Incasso"];

// Categorie Uscita (alfabetico)
const CATEGORIE_USCITA = [
  "Acquisti Merce", "Affitto Showroom", "Altre Spese", "Marketing & Pubblicità",
  "Manutenzione", "Spese Amministrative", "Spese Logistiche", 
  "Spese Trasferte", "Stipendi & Provvigioni", "Utenze"
];

function App() {
  const [serverReady, setServerReady] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [currentView, setCurrentView] = useState('home');
  const [appuntamentiFocus, setAppuntamentiFocus] = useState(null);
  const [ritornoQuintili, setRitornoQuintili] = useState(null);
  const [quintiliRipristino, setQuintiliRipristino] = useState(null);
  const [currentLinea, setCurrentLinea] = useState('sarabanda');
  const [menuSarabandaOpen, setMenuSarabandaOpen] = useState(false);
  const [menuIDOOpen, setMenuIDOOpen] = useState(false);
  const [menuEsteroOpen, setMenuEsteroOpen] = useState(false);
  const [menuShowroomOpen, setMenuShowroomOpen] = useState(false);
  const [menuCreditoOpen, setMenuCreditoOpen] = useState(false);
  const [menuDdtOpen, setMenuDdtOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false); // Mobile
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);

  // PWA Update Banner
  const [showUpdateBanner, setShowUpdateBanner] = useState(false);
  const waitingWorkerRef = React.useRef(null);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const checkForUpdate = () => {
      navigator.serviceWorker.getRegistration().then(reg => {
        if (!reg) return;
        reg.update();
        const onUpdateFound = () => {
          const newSW = reg.installing;
          if (!newSW) return;
          newSW.addEventListener('statechange', () => {
            if (newSW.state === 'installed' && navigator.serviceWorker.controller) {
              waitingWorkerRef.current = newSW;
              setShowUpdateBanner(true);
            }
          });
        };
        reg.addEventListener('updatefound', onUpdateFound);
        // Check if there's already a waiting worker
        if (reg.waiting && navigator.serviceWorker.controller) {
          waitingWorkerRef.current = reg.waiting;
          setShowUpdateBanner(true);
        }
      });
    };
    checkForUpdate();
    const interval = setInterval(checkForUpdate, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleUpdateApp = () => {
    if (waitingWorkerRef.current) {
      waitingWorkerRef.current.postMessage({ type: 'SKIP_WAITING' });
    }
    setShowUpdateBanner(false);
    window.location.reload();
  };

  const handleServerReady = useCallback(() => setServerReady(true), []);

  // Nascondi banner Emergent "Frontend Preview" da dentro React
  useEffect(() => {
    const nukeEmergentBanner = () => {
      document.querySelectorAll('body > *').forEach(el => {
        const id = el.id || '';
        const tag = el.tagName || '';
        if (id === 'root' || id === 'native-splash' || tag === 'SCRIPT' || tag === 'NOSCRIPT' || tag === 'LINK' || tag === 'STYLE') return;
        const text = (el.textContent || '').toLowerCase();
        if (text.includes('frontend preview') || text.includes('wake up') || text.includes('wake server') || text.includes('enable backend')) {
          el.style.cssText = 'display:none!important;visibility:hidden!important;opacity:0!important;position:absolute!important;top:-99999px!important;pointer-events:none!important;';
          // Auto-click Wake Up
          el.querySelectorAll('button').forEach(btn => {
            if (btn.textContent.toLowerCase().includes('wake')) {
              try { btn.click(); } catch (e) { /* wake button non cliccabile: ignore */ }
            }
          });
        }
      });
    };
    nukeEmergentBanner();
    const interval = setInterval(nukeEmergentBanner, 500);
    const observer = new MutationObserver(nukeEmergentBanner);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => { clearInterval(interval); observer.disconnect(); };
  }, []);

  // Tema chiaro/scuro
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'dark');
  
  useEffect(() => {
    document.body.classList.toggle('light-theme', theme === 'light');
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => setTheme(prev => prev === 'dark' ? 'light' : 'dark');

  // Keep-alive ping ogni 25 secondi + raffica di ping quando il tab torna visibile
  useEffect(() => {
    if (!serverReady) return;
    const ping = () => fetch('/api/health', { cache: 'no-store' }).catch(() => {});
    
    const interval = setInterval(ping, 25 * 1000);
    
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        // Raffica: 3 ping ravvicinati per svegliare il server piu in fretta
        ping();
        setTimeout(ping, 500);
        setTimeout(ping, 1500);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [serverReady]);

  // Data states
  const [statsSarabanda, setStatsSarabanda] = useState(null);
  const [statsIDO, setStatsIDO] = useState(null);
  const [contatti, setContatti] = useState([]);
  const [amministrazione, setAmministrazione] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [recruiting, setRecruiting] = useState([]);
  const [progetti, setProgetti] = useState([]);
  const [entrate, setEntrate] = useState([]);
  const [uscite, setUscite] = useState([]);
  const [statsContabilita, setStatsContabilita] = useState(null);

  // Modal & Form states
  const [selectedProvincia, setSelectedProvincia] = useState('Milano');

  // Pull to refresh (PWA)
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const pullRef = React.useRef({ startY: 0, startX: 0, pulling: false, locked: false, distance: 0, refreshing: false });
  const viewRef = React.useRef(currentView);
  const lineaRef = React.useRef(currentLinea);
  React.useEffect(() => { viewRef.current = currentView; }, [currentView]);

  // Forza scroll al top quando si cambia pagina (tutte le sezioni partono dall'alto)
  useEffect(() => {
    const resetScroll = () => {
      try {
        // Il vero container scrollabile e' #root (position:fixed + overflow-y:auto in index.css)
        const root = document.getElementById('root');
        if (root) root.scrollTop = 0;
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
        if (document.documentElement) document.documentElement.scrollTop = 0;
        if (document.body) document.body.scrollTop = 0;
        document.querySelectorAll('.main-content, .app-container, .section-view').forEach(el => {
          try { el.scrollTop = 0; } catch { /* noop */ }
        });
      } catch { /* noop */ }
    };
    resetScroll();
    // Secondo tentativo dopo il render del lazy-loaded component
    const t1 = setTimeout(resetScroll, 50);
    const t2 = setTimeout(resetScroll, 250);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [currentView, currentLinea]);
  React.useEffect(() => { lineaRef.current = currentLinea; }, [currentLinea]);

  useEffect(() => {
    const ref = pullRef.current;

    const hasScrollableParent = (el) => {
      let node = el;
      while (node && node !== document.body) {
        if (node.scrollTop > 0) return true;
        const style = window.getComputedStyle(node);
        const overflow = style.overflowY;
        if ((overflow === 'auto' || overflow === 'scroll') && node.scrollHeight > node.clientHeight && node.scrollTop > 0) {
          return true;
        }
        node = node.parentElement;
      }
      return false;
    };

    const onStart = (e) => {
      ref.locked = false;
      ref.pulling = false;
      ref.distance = 0;
      if (window.scrollY <= 0 && !ref.refreshing && !hasScrollableParent(e.target)) {
        ref.startY = e.touches[0].clientY;
        ref.startX = e.touches[0].clientX;
      }
    };

    const onMove = (e) => {
      if (ref.refreshing || ref.locked || ref.startY === 0) return;

      const currentY = e.touches[0].clientY;
      const currentX = e.touches[0].clientX;
      const diffY = currentY - ref.startY;
      const diffX = Math.abs(currentX - ref.startX);

      // Se lo scroll è orizzontale o verso l'alto, blocca definitivamente questo gesto
      if (!ref.pulling) {
        if (diffX > 15 || diffY < 0) {
          ref.locked = true;
          return;
        }
        // Richiedi almeno 40px di trascinamento verso il basso prima di attivare
        if (diffY < 40 || window.scrollY > 0) return;
        ref.pulling = true;
      }

      if (ref.pulling && window.scrollY <= 0) {
        e.preventDefault();
        const activeDiff = diffY - 40;
        const resistance = Math.max(0.25, 1 - (activeDiff / 400));
        const dist = Math.min(activeDiff * resistance, 120);
        ref.distance = dist;
        setPullDistance(dist);
      }
    };

    const onEnd = () => {
      if (!ref.pulling) {
        ref.startY = 0;
        return;
      }
      ref.pulling = false;
      ref.startY = 0;

      if (ref.distance >= 70 && !ref.refreshing) {
        ref.refreshing = true;
        setIsRefreshing(true);
        ref.distance = 55;
        setPullDistance(55);
        // Soft refresh: ricarica dati senza ricaricare la pagina (evita schermo nero)
        setTimeout(async () => {
          try {
            await loadInitialData();
            await loadDataForView(viewRef.current, lineaRef.current);
          } catch (e) { /* refresh error: silent */ }
          setRefreshKey(k => k + 1);
          ref.refreshing = false;
          setIsRefreshing(false);
          ref.distance = 0;
          setPullDistance(0);
        }, 400);
      } else {
        ref.distance = 0;
        setPullDistance(0);
      }
    };

    document.addEventListener('touchstart', onStart, { passive: true });
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onEnd);
    return () => {
      document.removeEventListener('touchstart', onStart);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
    };
  }, []);

  useEffect(() => {
    // Pulisci vecchio auth da localStorage (migrato a sessionStorage)
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // Nota: il listener CLEAR_OLD_AUTH è stato rimosso perche' causava race condition:
    // quando il nuovo SW si attivava subito dopo un login riuscito, svuotava il token
    // appena salvato e forzava un reload, lasciando l'utente bloccato sul loader.
  }, []);

  useEffect(() => {
    // Valida il token all'avvio — se scaduto/invalido, forza il login
    const validateSession = async () => {
      if (!isAuthenticated()) return;
      try {
        await api.get('/auth/me');
        setAuthenticated(true);
        setUser(getUser());
        loadInitialData();
        // Carica le stagioni di default configurate (dashboard/appuntamenti/... e bloccati)
        // e le salva in localStorage così getStagioneCorrente/getStagioneBloccati
        // possono leggerle sincronamente.
        fetchStagioniDefault().catch(() => { /* fallback ai valori hardcoded */ });
      } catch (err) {
        // Token invalido/scaduto — pulisci e mostra login
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
      }
    };
    validateSession();
  }, []);

  // Lock body scroll when modal is open (iOS-safe)
  useEffect(() => {
    let scrollY = 0;
    let isLocked = false;
    const lockBody = () => {
      const hasModal = document.querySelector('.modal-overlay');
      if (hasModal && !isLocked) {
        scrollY = window.scrollY;
        isLocked = true;
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.left = '0';
        document.body.style.right = '0';
        document.body.style.overflow = 'hidden';
      } else if (!hasModal && isLocked) {
        isLocked = false;
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      }
    };
    const observer = new MutationObserver(lockBody);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => { observer.disconnect(); document.body.style.position = ''; document.body.style.top = ''; document.body.style.left = ''; document.body.style.right = ''; document.body.style.overflow = ''; };
  }, []);

  // Auto-scroll menu item into view quando cambia la vista
  // IMPORTANTE: usa scrollTop sul container del menu, NON scrollIntoView (che sposterebbe anche lo scroll della pagina su mobile)
  useEffect(() => {
    if (!authenticated || !currentView) return;
    const activeBtn = document.querySelector('.nav-subitem.active');
    if (!activeBtn) return;
    // Trova il container scrollabile del menu (sidebar), non usa scrollIntoView per non spostare la pagina
    const container = activeBtn.closest('.sidebar-nav, .sidebar, .nav-menu');
    if (!container) return;
    const cRect = container.getBoundingClientRect();
    const bRect = activeBtn.getBoundingClientRect();
    if (bRect.top < cRect.top || bRect.bottom > cRect.bottom) {
      container.scrollTop += (bRect.top - cRect.top) - (cRect.height / 2 - bRect.height / 2);
    }
  }, [currentView, authenticated]);

  const loadInitialData = async () => {
    try {
      const [sarabandaStats, idoStats] = await Promise.all([
        api.get('/sarabanda/stats'),
        api.get('/ido/stats')
      ]);
      setStatsSarabanda(sarabandaStats.data);
      setStatsIDO(idoStats.data);
    } catch (error) {
      console.error('Error loading initial data:', error);
    }
  };

  const handleLoginSuccess = () => {
    setAuthenticated(true);
    setUser(getUser());
    loadInitialData();
    fetchStagioniDefault().catch(() => { /* fallback ai valori hardcoded */ });

    // Register push notifications after login
    const token = sessionStorage.getItem('token');
    if (token) {
      subscribeToPush(token);
    }
  };

  const handleLogout = () => {
    clearAuth();
    setAuthenticated(false);
    setUser(null);
  };

  const isCliente = user?.ruolo === 'cliente_key_account';

  const handleMenuClick = (view, linea = null) => {
    setCurrentView(view);
    setRitornoQuintili(null);
    if (linea) setCurrentLinea(linea);
    setSidebarOpen(false); // Close on mobile
    window.scrollTo(0, 0); // Scroll to top on view change
    // Mantieni aperto il sottomenu DDT quando navighi verso una delle sue sottoviste
    if (view === 'ddt-entrata' || view === 'ddt-uscita') {
      setMenuDdtOpen(true);
    }
    // Mantieni aperto il sottomenu Credito quando navighi a bloccati o gestione effetti
    if (view === 'bloccati' || view === 'gestione-effetti-unificato') {
      setMenuCreditoOpen(true);
    }
    // Load data based on view
    loadDataForView(view, linea || currentLinea);
  };

  const loadDataForView = async (view, linea) => {
    const viewsNeedingData = ['contatti', 'amministrazione', 'gestione', 'recruiting', 'progetti'];
    if (!viewsNeedingData.includes(view)) return;
    setLoading(true);
    try {
      switch(view) {
        case 'contatti':
          const contattiRes = await api.get(`/contatti/${linea}`);
          setContatti(contattiRes.data);
          break;
        case 'amministrazione':
          const amministrazioneRes = await api.get(`/amministrazione/${linea}`);
          setAmministrazione(amministrazioneRes.data);
          break;
        case 'gestione':
          const clientiRes = await api.get(`/${linea.toLowerCase()}/clienti`);
          setClienti(clientiRes.data);
          break;
        case 'recruiting':
          const recruitingRes = await api.get(`/recruiting/${linea}`);
          setRecruiting(recruitingRes.data);
          break;
        case 'progetti':
          const progettiRes = await api.get(`/progetti/${linea}`);
          setProgetti(progettiRes.data);
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!serverReady) {
    return <SplashScreen onReady={handleServerReady} />;
  }

  if (!authenticated) {
    return <Login onLoginSuccess={handleLoginSuccess} />;
  }

  return (
    <ContattiProvider>
      <PdfViewerProvider>
      <Toaster position="top-center" theme="dark" richColors closeButton duration={3500}
        offset="calc(env(safe-area-inset-top, 0px) + 16px)"
        toastOptions={{ style: { marginTop: 'env(safe-area-inset-top, 0px)' } }} />
      <div className="app-container">
      {/* Pull to refresh indicator */}
      {pullDistance > 0 && (() => {
        const progress = Math.min(pullDistance / 70, 1);
        const ready = progress >= 1;
        const indicatorY = Math.min(pullDistance * 0.6, 80);
        return (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0,
          zIndex: 9999, display: 'flex', justifyContent: 'center',
          paddingTop: `calc(env(safe-area-inset-top, 0px) + ${indicatorY}px)`,
          transition: pullDistance === 0 ? 'padding-top 0.35s cubic-bezier(0.2, 0, 0, 1)' : 'none',
          pointerEvents: 'none'
        }}>
          <div style={{
            width: '36px', height: '36px', borderRadius: '50%',
            background: ready ? '#10b981' : 'rgba(255,255,255,0.1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transform: `scale(${0.6 + progress * 0.4})`,
            opacity: Math.min(progress * 1.5, 1),
            transition: pullDistance === 0 ? 'all 0.35s cubic-bezier(0.2, 0, 0, 1)' : 'background 0.15s',
            boxShadow: ready ? '0 0 16px rgba(16,185,129,0.4)' : 'none'
          }}>
            {isRefreshing ? (
              <div style={{ width: '16px', height: '16px', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.5s linear infinite' }} />
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={ready ? '#fff' : 'rgba(255,255,255,0.7)'}
                strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                style={{ transform: `rotate(${progress * 180}deg)`, transition: 'stroke 0.15s' }}>
                <path d="M12 5v14M5 12l7 7 7-7" />
              </svg>
            )}
          </div>
        </div>
        );
      })()}

      {/* Banner aggiornamento PWA */}
      {showUpdateBanner && (
        <div
          data-testid="update-banner"
          style={{
            position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
            zIndex: 99999, display: 'flex', alignItems: 'center', gap: 12,
            background: '#1a1a2e', border: '1px solid #2a2a4a',
            borderRadius: 12, padding: '12px 20px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
            animation: 'slideUpBanner 0.4s ease-out',
            maxWidth: 'calc(100vw - 32px)',
          }}
        >
          <span style={{ fontSize: 13, color: '#e0e0e0', whiteSpace: 'nowrap' }}>
            Nuova versione disponibile
          </span>
          <button
            onClick={handleUpdateApp}
            data-testid="update-banner-btn"
            style={{
              padding: '6px 16px', borderRadius: 8,
              background: '#3b82f6', color: 'white',
              border: 'none', cursor: 'pointer',
              fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
            }}
          >
            Aggiorna ora
          </button>
          <button
            onClick={() => setShowUpdateBanner(false)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              padding: 4, display: 'flex', color: '#666',
            }}
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Mobile Header */}
      <div className="mobile-header">
        <button 
          className="mobile-menu-button" 
          onClick={() => setSidebarOpen(!sidebarOpen)}
          aria-label="Toggle menu"
        >
          {sidebarOpen ? <X size={30} /> : <Menu size={30} />}
        </button>
        
        <img 
          src={theme === 'light' ? "/logo-rubbi-official.jpg" : "/logo-rubbi-new.png"} 
          alt="Rubbi Milano" 
          className="mobile-logo-large logo-clickable" 
          onClick={() => handleMenuClick('home')}
          style={{ cursor: 'pointer' }}
        />
        
        <div className="mobile-header-actions">
          <button 
            className="theme-toggle-btn"
            onClick={toggleTheme}
            title={theme === 'dark' ? 'Tema chiaro' : 'Tema scuro'}
            data-testid="theme-toggle"
          >
            {theme === 'dark' ? <Sun size={26} /> : <Moon size={26} />}
          </button>
          <button 
            className="search-button"
            onClick={() => {
              if ('Notification' in window) {
                if (Notification.permission === 'default') {
                  Notification.requestPermission();
                }
              }
            }}
            title="Notifiche"
          >
            <Bell size={26} />
          </button>
          <button 
            className="search-button"
            onClick={() => handleMenuClick('ricerca')}
            title="Cerca"
          >
            <Search size={26} />
          </button>
        </div>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="sidebar-overlay show" 
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <button 
          className="sidebar-close-button" 
          onClick={() => setSidebarOpen(false)}
          aria-label="Chiudi menu"
        >
          <X size={24} />
        </button>
        
        <div className="logo-container" onClick={() => handleMenuClick('home')} style={{ cursor: 'pointer' }}>
          <img src={theme === 'light' ? "/logo-rubbi-official.jpg" : "/logo-rubbi-new.png"} alt="Show-room Rubbi Milano" className="logo" />
          <h2 className="portal-title">Show-room<br/>Rubbi Milano</h2>
        </div>
        
        <nav className="nav-menu">
          <button
            onClick={() => handleMenuClick('home')}
            className={`nav-item ${currentView === 'home' ? 'active' : ''}`}
            data-testid="nav-home"
          >
            <Home size={20} />
            <span>Home</span>
          </button>

          {/* Area Generale — sezioni comuni NON legate a un brand */}
          {!isCliente && (
          <div className="nav-section">
            <button
              type="button"
              data-testid="nav-group-generale"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setMenuShowroomOpen(prev => !prev);
              }}
              className="nav-section-header"
            >
              <span className="nav-section-title">Area Generale</span>
              {menuShowroomOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
            </button>

            {menuShowroomOpen && (
              <div className="nav-submenu">
                <button
                  onClick={() => handleMenuClick('agenda')}
                  className={`nav-subitem ${currentView === 'agenda' ? 'active' : ''}`}
                >
                  <Calendar size={18} />
                  <span>Agenda</span>
                </button>
                <button
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setMenuCreditoOpen(prev => !prev); }}
                  className={`nav-subitem ${(currentView === 'bloccati' || currentView === 'gestione-effetti-unificato') ? 'active' : ''}`}
                  data-testid="nav-credito-toggle"
                >
                  <Lock size={18} />
                  <span style={{ flex: 1, textAlign: 'left' }}>Credito</span>
                  {menuCreditoOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </button>
                {menuCreditoOpen && (
                  <div className="rc-slide-down" style={{ paddingLeft: '18px', borderLeft: '1px dashed var(--border-faint)', marginLeft: '16px' }}>
                    <button
                      onClick={() => handleMenuClick('bloccati')}
                      className={`nav-subitem ${currentView === 'bloccati' ? 'active' : ''}`}
                      data-testid="nav-bloccati"
                    >
                      <Lock size={16} />
                      <span>Gestione Bloccati</span>
                    </button>
                    <button
                      onClick={() => handleMenuClick('gestione-effetti-unificato')}
                      className={`nav-subitem ${currentView === 'gestione-effetti-unificato' ? 'active' : ''}`}
                      data-testid="nav-gestione-effetti"
                    >
                      <FileText size={16} />
                      <span>Gestione Effetti</span>
                    </button>
                  </div>
                )}
                <button
                  onClick={() => handleMenuClick('contabilita')}
                  className={`nav-subitem ${currentView === 'contabilita' ? 'active' : ''}`}
                >
                  <TrendingUp size={18} />
                  <span>Contabilità</span>
                </button>
                <button
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setMenuDdtOpen(prev => !prev); }}
                  className={`nav-subitem ${(currentView === 'ddt-entrata' || currentView === 'ddt-uscita') ? 'active' : ''}`}
                  data-testid="nav-ddt-toggle"
                >
                  <FileText size={18} />
                  <span style={{ flex: 1, textAlign: 'left' }}>DDT</span>
                  {menuDdtOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </button>
                {menuDdtOpen && (
                  <div style={{ paddingLeft: '18px', borderLeft: '1px dashed var(--border-faint)', marginLeft: '16px' }}>
                    <button
                      onClick={() => handleMenuClick('ddt-entrata')}
                      className={`nav-subitem ${currentView === 'ddt-entrata' ? 'active' : ''}`}
                      data-testid="nav-ddt-entrata"
                    >
                      <FileText size={16} />
                      <span>DDT in Entrata</span>
                    </button>
                    <button
                      onClick={() => handleMenuClick('ddt-uscita')}
                      className={`nav-subitem ${currentView === 'ddt-uscita' ? 'active' : ''}`}
                      data-testid="nav-ddt-uscita"
                    >
                      <FileText size={16} />
                      <span>DDT in Uscita</span>
                    </button>
                  </div>
                )}
                <button
                  onClick={() => handleMenuClick('gestione-showroom')}
                  className={`nav-subitem ${currentView === 'gestione-showroom' ? 'active' : ''}`}
                >
                  <Package size={18} />
                  <span>Gestione Show-room</span>
                </button>
              </div>
            )}
          </div>
          )}

          {/* iDO */}
          {!isCliente && (
          <div className="nav-section">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setMenuIDOOpen(prev => !prev);
              }}
              className="nav-section-header"
            >
              <span className="nav-section-title">iDO</span>
              {menuIDOOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
            </button>
            
            {menuIDOOpen && (
              <div className="nav-submenu">
                <button
                  onClick={() => handleMenuClick('amministrazione', 'iDO')}
                  className={`nav-subitem ${currentView === 'amministrazione' && currentLinea === 'iDO' ? 'active' : ''}`}
                >
                  <CreditCard size={18} />
                  <span>Amministrazione</span>
                </button>
                <button
                  onClick={() => handleMenuClick('appuntamenti', 'iDO')}
                  className={`nav-subitem ${currentView === 'appuntamenti' && currentLinea === 'iDO' ? 'active' : ''}`}
                >
                  <CheckSquare size={18} />
                  <span>Appuntamenti</span>
                </button>
                <button
                  onClick={() => handleMenuClick('budget', 'iDO')}
                  className={`nav-subitem ${currentView === 'budget' && currentLinea === 'iDO' ? 'active' : ''}`}
                  data-testid="nav-budget-ido"
                >
                  <DollarSign size={18} />
                  <span>Budget</span>
                </button>
                <button
                  onClick={() => handleMenuClick('multi-linea-clienti', 'iDO')}
                  className={`nav-subitem ${currentView === 'multi-linea-clienti' && currentLinea === 'iDO' ? 'active' : ''}`}
                  data-testid="nav-multi-linea-clienti-ido"
                >
                  <Users size={18} />
                  <span>Clienti Multi-Linea</span>
                </button>
                <button
                  onClick={() => handleMenuClick('contatti', 'iDO')}
                  className={`nav-subitem ${currentView === 'contatti' && currentLinea === 'iDO' ? 'active' : ''}`}
                >
                  <Users size={18} />
                  <span>Contatti Clienti</span>
                </button>
                <button
                  onClick={() => handleMenuClick('progetti', 'iDO')}
                  className={`nav-subitem ${currentView === 'progetti' && currentLinea === 'iDO' ? 'active' : ''}`}
                >
                  <Target size={18} />
                  <span>Key Accounts</span>
                </button>
                <button
                  onClick={() => handleMenuClick('mappa-clienti', 'iDO')}
                  className={`nav-subitem ${currentView === 'mappa-clienti' && currentLinea === 'iDO' ? 'active' : ''}`}
                  data-testid="nav-mappa-ido"
                >
                  <MapPin size={18} />
                  <span>Mappa Clienti</span>
                </button>
                <button
                  onClick={() => handleMenuClick('gestione', 'iDO')}
                  className={`nav-subitem ${currentView === 'gestione' && currentLinea === 'iDO' ? 'active' : ''}`}
                >
                  <UserCheck size={18} />
                  <span>Ordini Clienti</span>
                </button>
                <button
                  onClick={() => handleMenuClick('quintili', 'iDO')}
                  className={`nav-subitem ${currentView === 'quintili' && currentLinea === 'iDO' ? 'active' : ''}`}
                  data-testid="nav-quintili-ido"
                >
                  <BarChart3 size={18} />
                  <span>Quintili</span>
                </button>
                <button
                  onClick={() => handleMenuClick('recruiting', 'iDO')}
                  className={`nav-subitem ${currentView === 'recruiting' && currentLinea === 'iDO' ? 'active' : ''}`}
                >
                  <UserPlus size={18} />
                  <span>Recruiting</span>
                </button>
                <button
                  onClick={() => handleMenuClick('riassortimenti', 'iDO')}
                  className={`nav-subitem ${currentView === 'riassortimenti' && currentLinea === 'iDO' ? 'active' : ''}`}
                >
                  <ShoppingBag size={18} />
                  <span>Riassortimenti</span>
                </button>
                <button
                  onClick={() => handleMenuClick('riepilogo', 'iDO')}
                  className={`nav-subitem ${currentView === 'riepilogo' && currentLinea === 'iDO' ? 'active' : ''}`}
                >
                  <BarChart3 size={18} />
                  <span>Riepilogo Clienti</span>
                </button>
                <button
                  onClick={() => handleMenuClick('schede-commerciali', 'iDO')}
                  className={`nav-subitem ${currentView === 'schede-commerciali' && currentLinea === 'iDO' ? 'active' : ''}`}
                  data-testid="nav-schede-ido"
                >
                  <FileText size={18} />
                  <span>Schede Commerciali</span>
                </button>
              </div>
            )}
          </div>
          )}

          {/* Sarabanda */}
          {!isCliente && (
          <div className="nav-section">
            <button
              type="button"
              data-testid="nav-group-sarabanda"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setMenuSarabandaOpen(prev => !prev);
              }}
              className="nav-section-header"
            >
              <span className="nav-section-title">Sarabanda</span>
              {menuSarabandaOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
            </button>
            
            {menuSarabandaOpen && (
              <div className="nav-submenu">
                <button
                  onClick={() => handleMenuClick('amministrazione', 'Sarabanda')}
                  className={`nav-subitem ${currentView === 'amministrazione' && currentLinea === 'Sarabanda' ? 'active' : ''}`}
                >
                  <CreditCard size={18} />
                  <span>Amministrazione</span>
                </button>
                <button
                  onClick={() => handleMenuClick('appuntamenti', 'Sarabanda')}
                  className={`nav-subitem ${currentView === 'appuntamenti' && currentLinea === 'Sarabanda' ? 'active' : ''}`}
                >
                  <CheckSquare size={18} />
                  <span>Appuntamenti</span>
                </button>
                <button
                  onClick={() => handleMenuClick('budget', 'Sarabanda')}
                  className={`nav-subitem ${currentView === 'budget' && currentLinea === 'Sarabanda' ? 'active' : ''}`}
                  data-testid="nav-budget-sarabanda"
                >
                  <DollarSign size={18} />
                  <span>Budget</span>
                </button>
                <button
                  onClick={() => handleMenuClick('multi-linea-clienti', 'Sarabanda')}
                  className={`nav-subitem ${currentView === 'multi-linea-clienti' && currentLinea === 'Sarabanda' ? 'active' : ''}`}
                  data-testid="nav-multi-linea-clienti-sarabanda"
                >
                  <Users size={18} />
                  <span>Clienti Multi-Linea</span>
                </button>
                <button
                  data-testid="nav-contatti-sarabanda"
                  onClick={() => handleMenuClick('contatti', 'Sarabanda')}
                  className={`nav-subitem ${currentView === 'contatti' && currentLinea === 'Sarabanda' ? 'active' : ''}`}
                >
                  <Users size={18} />
                  <span>Contatti Clienti</span>
                </button>
                <button
                  onClick={() => handleMenuClick('progetti', 'Sarabanda')}
                  className={`nav-subitem ${currentView === 'progetti' && currentLinea === 'Sarabanda' ? 'active' : ''}`}
                >
                  <Target size={18} />
                  <span>Key Accounts</span>
                </button>
                <button
                  onClick={() => handleMenuClick('mappa-clienti', 'Sarabanda')}
                  className={`nav-subitem ${currentView === 'mappa-clienti' && currentLinea === 'Sarabanda' ? 'active' : ''}`}
                  data-testid="nav-mappa-sarabanda"
                >
                  <MapPin size={18} />
                  <span>Mappa Clienti</span>
                </button>
                <button
                  onClick={() => handleMenuClick('gestione', 'Sarabanda')}
                  className={`nav-subitem ${currentView === 'gestione' && currentLinea === 'Sarabanda' ? 'active' : ''}`}
                >
                  <UserCheck size={18} />
                  <span>Ordini Clienti</span>
                </button>
                <button
                  onClick={() => handleMenuClick('quintili', 'Sarabanda')}
                  className={`nav-subitem ${currentView === 'quintili' && currentLinea === 'Sarabanda' ? 'active' : ''}`}
                  data-testid="nav-quintili-sarabanda"
                >
                  <BarChart3 size={18} />
                  <span>Quintili</span>
                </button>
                <button
                  onClick={() => handleMenuClick('recruiting', 'Sarabanda')}
                  className={`nav-subitem ${currentView === 'recruiting' && currentLinea === 'Sarabanda' ? 'active' : ''}`}
                >
                  <UserPlus size={18} />
                  <span>Recruiting</span>
                </button>
                <button
                  onClick={() => handleMenuClick('riassortimenti', 'Sarabanda')}
                  className={`nav-subitem ${currentView === 'riassortimenti' && currentLinea === 'Sarabanda' ? 'active' : ''}`}
                >
                  <ShoppingBag size={18} />
                  <span>Riassortimenti</span>
                </button>
                <button
                  onClick={() => handleMenuClick('riepilogo', 'Sarabanda')}
                  className={`nav-subitem ${currentView === 'riepilogo' && currentLinea === 'Sarabanda' ? 'active' : ''}`}
                >
                  <BarChart3 size={18} />
                  <span>Riepilogo Clienti</span>
                </button>
                <button
                  onClick={() => handleMenuClick('schede-commerciali', 'Sarabanda')}
                  className={`nav-subitem ${currentView === 'schede-commerciali' && currentLinea === 'Sarabanda' ? 'active' : ''}`}
                  data-testid="nav-schede-sarabanda"
                >
                  <FileText size={18} />
                  <span>Schede Commerciali</span>
                </button>
              </div>
            )}
          </div>
          )}

          {/* ESTERO — replica delle sotto-sezioni con linea="Estero" */}
          {!isCliente && (
          <div className={`nav-section ${menuEsteroOpen ? 'expanded' : ''}`}>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setMenuEsteroOpen(prev => !prev);
              }}
              className="nav-section-header"
              data-testid="nav-estero-toggle"
            >
              <span className="nav-section-title">Estero</span>
              {menuEsteroOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
            </button>

            {menuEsteroOpen && (
              <div className="nav-submenu">
                <button onClick={() => handleMenuClick('amministrazione', 'Estero')} className={`nav-subitem ${currentView === 'amministrazione' && currentLinea === 'Estero' ? 'active' : ''}`}><CreditCard size={18} /><span>Amministrazione</span></button>
                <button onClick={() => handleMenuClick('appuntamenti', 'Estero')} className={`nav-subitem ${currentView === 'appuntamenti' && currentLinea === 'Estero' ? 'active' : ''}`}><CheckSquare size={18} /><span>Appuntamenti</span></button>
                <button onClick={() => handleMenuClick('budget', 'Estero')} className={`nav-subitem ${currentView === 'budget' && currentLinea === 'Estero' ? 'active' : ''}`}><DollarSign size={18} /><span>Budget</span></button>
                <button onClick={() => handleMenuClick('contatti', 'Estero')} className={`nav-subitem ${currentView === 'contatti' && currentLinea === 'Estero' ? 'active' : ''}`} data-testid="nav-contatti-estero"><Users size={18} /><span>Contatti Clienti</span></button>
                <button onClick={() => handleMenuClick('progetti', 'Estero')} className={`nav-subitem ${currentView === 'progetti' && currentLinea === 'Estero' ? 'active' : ''}`}><Target size={18} /><span>Key Accounts</span></button>
                <button onClick={() => handleMenuClick('mappa-clienti', 'Estero')} className={`nav-subitem ${currentView === 'mappa-clienti' && currentLinea === 'Estero' ? 'active' : ''}`}><MapPin size={18} /><span>Mappa Clienti</span></button>
                <button onClick={() => handleMenuClick('gestione', 'Estero')} className={`nav-subitem ${currentView === 'gestione' && currentLinea === 'Estero' ? 'active' : ''}`}><UserCheck size={18} /><span>Ordini Clienti</span></button>
                <button onClick={() => handleMenuClick('recruiting', 'Estero')} className={`nav-subitem ${currentView === 'recruiting' && currentLinea === 'Estero' ? 'active' : ''}`}><UserPlus size={18} /><span>Recruiting</span></button>
                <button onClick={() => handleMenuClick('riassortimenti', 'Estero')} className={`nav-subitem ${currentView === 'riassortimenti' && currentLinea === 'Estero' ? 'active' : ''}`}><ShoppingBag size={18} /><span>Riassortimenti</span></button>
                <button onClick={() => handleMenuClick('riepilogo', 'Estero')} className={`nav-subitem ${currentView === 'riepilogo' && currentLinea === 'Estero' ? 'active' : ''}`}><BarChart3 size={18} /><span>Riepilogo Clienti</span></button>
                <button onClick={() => handleMenuClick('schede-commerciali', 'Estero')} className={`nav-subitem ${currentView === 'schede-commerciali' && currentLinea === 'Estero' ? 'active' : ''}`}><FileText size={18} /><span>Schede Commerciali</span></button>
              </div>
            )}
          </div>
          )}

          {/* Menu semplificato per Cliente Key Account */}
          {isCliente && user?.progetto_id && (
            <button
              onClick={() => {
                handleMenuClick('progetti', currentLinea || 'iDO');
              }}
              className={`nav-item ${currentView === 'progetti' ? 'active' : ''}`}
              data-testid="nav-mio-progetto"
            >
              <Target size={20} />
              <span>Il Mio Negozio</span>
            </button>
          )}
        </nav>
        
        <div className="sidebar-footer">
          <div className="user-info">
            <User size={16} />
            <span>{user?.nome}</span>
          </div>
          <button
            onClick={() => handleMenuClick('impostazioni')}
            className={`nav-item ${currentView === 'impostazioni' ? 'active' : ''}`}
            style={{ 
              width: '100%', 
              marginBottom: '8px',
              justifyContent: 'flex-start'
            }}
          >
            <Settings size={16} />
            <span>Impostazioni</span>
          </button>
          <button onClick={handleLogout} className="btn-logout" data-testid="btn-logout">
            <LogOut size={16} />
            <span>Esci</span>
          </button>
          <div data-testid="app-version" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '8px', padding: '6px 0' }}>
            <span style={{ fontSize: '10px', color: 'var(--text-faint)', letterSpacing: '0.5px' }}>v3.8.2</span>
            <button
              data-testid="btn-force-update"
              onClick={async () => {
                if ('serviceWorker' in navigator) {
                  const regs = await navigator.serviceWorker.getRegistrations();
                  for (const reg of regs) { await reg.unregister(); }
                }
                if ('caches' in window) {
                  const keys = await caches.keys();
                  for (const k of keys) { await caches.delete(k); }
                }
                window.location.reload(true);
              }}
              style={{ fontSize: '10px', color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
            >
              Forza aggiornamento
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content">

        {/* Nota: il loader globale e' rimosso. Il Suspense fallback <SectionLoader /> sotto
            mostra una sola rotella durante il lazy-load delle sezioni. */}
        {currentView === 'home' && (
            <HomeView onNavigate={handleMenuClick} />
        )}
        
        <ErrorBoundary key={currentView + '-' + currentLinea + '-' + refreshKey}>
        <Suspense fallback={<SectionLoader />}>
        {currentView === 'gestione-effetti-unificato' && <GestioneEffettiUnificato />}

        {currentView === 'contatti' && (
          <ContattiClientiSection
            linea={currentLinea}
            contatti={contatti}
            onReload={() => loadDataForView('contatti', currentLinea)}
          />
        )}

        {currentView === 'amministrazione' && (
          <AmministrazioneSection
            linea={currentLinea}
            records={amministrazione}
            onReload={() => loadDataForView('amministrazione', currentLinea)}
          />
        )}

        {currentView === 'gestione' && (
          <GestioneClientiSection
            linea={currentLinea}
            clienti={clienti}
            onReload={() => {
              loadDataForView('gestione', currentLinea);
              loadInitialData();
            }}
          />
        )}

        {currentView === 'recruiting' && (
          <RecruitingSection
            linea={currentLinea}
            recruiting={recruiting}
            onReload={() => loadDataForView('recruiting', currentLinea)}
          />
        )}

        {currentView === 'calendario' && (
          <CalendarioSection linea={currentLinea} />
        )}
        {currentView === 'appuntamenti' && (
          <AppuntamentiSection
            linea={currentLinea}
            focus={appuntamentiFocus}
            onFocusConsumed={() => setAppuntamentiFocus(null)}
            onTornaQuintili={ritornoQuintili && ritornoQuintili.linea === currentLinea ? () => {
              const r = ritornoQuintili;
              handleMenuClick('quintili', r.linea);
              setQuintiliRipristino(r);
            } : null}
          />
        )}

        {currentView === 'progetti' && (
          <ProgettiSpecialiSection
            linea={currentLinea}
            progetti={progetti}
            onReload={() => loadDataForView('progetti', currentLinea)}
            currentUser={user}
          />
        )}

        {currentView === 'contabilita' && (
          <ContabilitaSection />
        )}

        {currentView === 'ddt-uscita' && (
          <DDTSection tipo="uscita" />
        )}
        {currentView === 'ddt-entrata' && (
          <DDTSection tipo="entrata" />
        )}

        {currentView === 'riepilogo' && (
          <RiepilogoClientiSection linea={currentLinea} />
        )}

        {currentView === 'multi-linea-clienti' && (
          <MultiLineaClientiSection />
        )}

        {currentView === 'quintili' && (
          <QuintiliSection
            linea={currentLinea}
            ripristino={quintiliRipristino}
            onRipristinoConsumed={() => setQuintiliRipristino(null)}
            onVaiAppuntamenti={(focus, ritorno) => {
              handleMenuClick('appuntamenti', currentLinea);
              setAppuntamentiFocus(focus);
              setRitornoQuintili(ritorno);
            }}
          />
        )}

        {currentView === 'riassortimenti' && (
          <RiassortimentiSection linea={currentLinea} />
        )}

        {currentView === 'bloccati' && <BloccatiSection />}
        {/* sospesi section removed - saldi importati direttamente in Bloccati */}

        {currentView === 'agenda' && (
          <CalendarioSection />
        )}

        {currentView === 'schede-commerciali' && (
          <SchedeCommercialiSection linea={currentLinea} />
        )}

        {currentView === 'mappa-clienti' && (
          <MappaClientiSection linea={currentLinea} />
        )}

        {currentView === 'budget' && (
          <BudgetSection linea={currentLinea} />
        )}

        {currentView === 'ricerca' && (
          <RicercaSection />
        )}

        {currentView === 'impostazioni' && <ImpostazioniSection />}
        {currentView === 'campionari' && <CampionariSection />}
        {currentView === 'gestione-showroom' && <GestioneShowroomSection />}
        </Suspense>
        </ErrorBoundary>
      </main>

    </div>
    </PdfViewerProvider>
    </ContattiProvider>
  );
}

export default App;

